import React from "react";
import { CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { CheckboxMultiSelect } from "@/components/custom/CheckboxMultiSelect";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { useTranslation } from "react-i18next";

interface RecommendedDoctorsSectionProps {
  patientId: string;
  assessmentId?: string;
  selectedDoctors: string[];
  onDoctorsChange: (doctorIds: string[]) => void;
}

export const RecommendedDoctorsSection: React.FC<
  RecommendedDoctorsSectionProps
> = ({ patientId, selectedDoctors, onDoctorsChange }) => {
  const { t } = useTranslation("patients");
  const { data: groups = [], isLoading: isGroupsLoading } = useGetGroupsQuery(
    undefined,
    { skip: !patientId },
  );
  const groupIds = groups.map((group) => group.id);
  const { data: doctors = [], isLoading: isDoctorsLoading } = useGetDoctorsQuery(
    { group_ids: groupIds },
    { skip: groupIds.length === 0 },
  );
  const isLoading = isGroupsLoading || isDoctorsLoading;

  const doctorOptions = doctors.map((doctor) => ({
    id: doctor.id,
    label: doctor.name,
    detail: doctor.role,
  }));

  return (
    <div className="border-gray-200">
      <div className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.recommendedDoctors.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.recommendedDoctors.subtitle")}
        </p>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("clinicalSections.recommendedDoctors.loadingDoctors")}
          </div>
        ) : groupIds.length === 0 || doctors.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {t("clinicalSections.recommendedDoctors.noDoctors")}
          </p>
        ) : (
          <CheckboxMultiSelect
            id="recommended-doctors"
            options={doctorOptions}
            value={selectedDoctors}
            onChange={onDoctorsChange}
            placeholder={t("clinicalSections.recommendedDoctors.selectDoctor")}
            selectedText={(count) => t("scheduling.doctorsSelected", { count })}
            emptyText={t("clinicalSections.recommendedDoctors.noDoctors")}
            removeLabel={(name) =>
              t("clinicalSections.recommendedDoctors.removeDoctor", { name })
            }
            triggerClassName="h-12"
          />
        )}
      </div>
    </div>
  );
};
